import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Box,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';

function TaxConfigurationList({ configurations, onEdit, onDelete, onSetDefault }) {
  if (!configurations || configurations.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          No tax configurations found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Click "Add Tax Configuration" to create your first tax setting
        </Typography>
      </Box>
    );
  }

  const formatPercentage = (rate) => {
    return `${parseFloat(rate).toFixed(2)}%`;
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(amount);
  };

  const getApplicabilityText = (config) => {
    const applicable = [];
    if (config.applyToConsultation) applicable.push('Consultation');
    if (config.applyToProcedure) applicable.push('Procedure');
    if (config.applyToMedication) applicable.push('Medication');
    if (config.applyToService) applicable.push('Service');
    return applicable.length > 0 ? applicable.join(', ') : 'None';
  };

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Tax Name</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Rate/Amount</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Default</TableCell>
            <TableCell>Applies To</TableCell>
            <TableCell>Thresholds</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {configurations.map((config) => (
            <TableRow key={config.taxConfigId} hover>
              <TableCell>
                <Box>
                  <Typography variant="subtitle2">
                    {config.taxName}
                  </Typography>
                  {config.description && (
                    <Typography variant="caption" color="text.secondary">
                      {config.description}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  label={config.taxType || 'Percentage'}
                  size="small"
                  color={config.taxType === 'Fixed' ? 'secondary' : 'primary'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                {config.taxType === 'Fixed' && config.fixedAmount
                  ? formatCurrency(config.fixedAmount)
                  : formatPercentage(config.taxRate)}
              </TableCell>
              <TableCell>
                <Chip
                  label={config.isActive ? 'Active' : 'Inactive'}
                  size="small"
                  color={config.isActive ? 'success' : 'default'}
                  variant={config.isActive ? 'filled' : 'outlined'}
                />
              </TableCell>
              <TableCell>
                <Tooltip title={config.isDefault ? 'Default tax' : 'Set as default'}>
                  <IconButton
                    onClick={() => !config.isDefault && onSetDefault(config)}
                    disabled={config.isDefault}
                    size="small"
                  >
                    {config.isDefault ? (
                      <StarIcon color="primary" />
                    ) : (
                      <StarBorderIcon />
                    )}
                  </IconButton>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Typography variant="caption">
                  {getApplicabilityText(config)}
                </Typography>
              </TableCell>
              <TableCell>
                <Box>
                  {config.minimumAmount && (
                    <Typography variant="caption" display="block">
                      Min: {formatCurrency(config.minimumAmount)}
                    </Typography>
                  )}
                  {config.maximumAmount && (
                    <Typography variant="caption" display="block">
                      Max: {formatCurrency(config.maximumAmount)}
                    </Typography>
                  )}
                  {!config.minimumAmount && !config.maximumAmount && (
                    <Typography variant="caption" color="text.secondary">
                      No limits
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Edit">
                    <IconButton
                      onClick={() => onEdit(config)}
                      size="small"
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      onClick={() => onDelete(config)}
                      size="small"
                      color="error"
                      disabled={config.isDefault}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default TaxConfigurationList;